import {
  getPreviewRecipes,
  PREFERENCE_TYPE_IDS,
  PreviewRecipe,
  PRODUCT_CATEGORY_IDS,
  PRODUCT_IDS,
  PRODUCT_TYPE_IDS,
} from "@chef/constants";
import {
  getNextCutoffs,
  getWeek,
  getYear,
  nextDeliveryWeekDateObj,
} from "@chef/helpers";
import {
  PreferencesQuery,
  api,
  getStaticPathsWithStore,
  storeWrapper,
} from "@chef/state-management";
import {
  getAttribute,
  getStandaloneProductCategoryIdsFromProductCategories,
} from "@chef/state-management/helpers";
import { nonNullable } from "@chef/utils/nonNullable";
import { IMealbox, getMealboxData } from "@chef/feature-sanity";
import { getSectionData } from "@chef/feature-sanity";
import { isEqualStrings } from "@chef/utils/equal";

export * from "./pages/WeekEditorPickAndMix";
export * from "./pages/OnTheMenu";
export * from "./pages/WeekEditorStandalone";
export * from "./pages/WeekEditorConfirmOrder";
export * from "./pages/WeekEditorRestartedBilling";
export * from "./pages/WeekEditorSuccess";
export * from "./pages/MealboxPage";
export * from "./lib/SimpleWeekSwitcherBar";
export { intl as weekEditorConfirmOrderIntl } from "./pages/WeekEditorConfirmOrder.Intl";
export { intl as weekEditorIntl } from "./pages/WeekEditorPickAndMix.Intl";
export { brand as weekEditorSuccessBrand } from "./pages/WeekEditorSuccess.Intl";
// TODO: Temporary for campaign pages -- should be removed once campaign is its own feature library
export * from "./hooks";
export * from "./utils";

export const WeekEditorGetStaticProps = storeWrapper.getStaticProps(
  (store) => async (ctx) => {
    const year = +(ctx.params?.["year"] || 0);
    const week = +(ctx.params?.["week"] || 0);

    store.dispatch(
      api.endpoints.pickAndMix.initiate({
        productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
        week,
        year,
      }),
    );

    store.dispatch(
      api.endpoints.productsByIds.initiate({
        productIds: [PRODUCT_IDS.ONESUB],
        week,
        year,
      }),
    );

    const { productCategories } = await store
      .dispatch(api.endpoints.productCategories.initiate())
      .unwrap();

    store.dispatch(
      api.endpoints.productsByCategories.initiate({
        categoryIds:
          getStandaloneProductCategoryIdsFromProductCategories(
            productCategories,
          ),
        week,
        year,
      }),
    );

    await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()));

    return {
      props: {
        year,
        week,
      },
      revalidate: 1800, // 30 minutes
    };
  },
);

export const WeekEditorGetStaticPaths = () => {
  const dates = getNextCutoffs();

  const paths = dates.map(({ week, year }) => {
    return { params: { week: week.toString(), year: year.toString() } };
  });

  return {
    paths,
    fallback: "blocking",
  };
};

// NOTE: we don't pre-fetch the data serverside here because it should already be cached
// from the previous page. So it'll be instant regardless
export const WeekEditorStandaloneGetStaticProps = storeWrapper.getStaticProps(
  () => async (ctx) => {
    const year = +(ctx.params?.["year"] || 0);
    const week = +(ctx.params?.["week"] || 0);

    const section = await getSectionData("week_editor_standalone_top", {
      preview: ctx.preview || false,
    });

    return {
      props: {
        year,
        week,
        section,
        draftMode: ctx.preview || false,
      },
    };
  },
);

export const WeekEditorStandaloneGetStaticPaths = () => {
  const dates = getNextCutoffs();

  const paths = dates.map(({ week, year }) => {
    return { params: { week: week.toString(), year: year.toString() } };
  });

  return {
    paths,
    fallback: "blocking",
  };
};

// NOTE: we don't pre-fetch the data serverside here because it should already be cached
// from the previous page. So it'll be instant regardless
export const WeekEditorConfirmOrderGetStaticProps = storeWrapper.getStaticProps(
  () => async (ctx) => {
    const year = +(ctx.params?.["year"] || 0);
    const week = +(ctx.params?.["week"] || 0);

    return {
      props: {
        year,
        week,
      },
    };
  },
);

export const WeekEditorConfirmOrderGetStaticPaths = () => {
  const dates = getNextCutoffs();

  const paths = dates.map(({ week, year }) => {
    return { params: { week: week.toString(), year: year.toString() } };
  });

  return {
    paths,
    fallback: "blocking",
  };
};

export const WeekEditorSuccessGetStaticProps = storeWrapper.getStaticProps(
  (store) => async (ctx) => {
    const year = +(ctx.params?.["year"] || 0);
    const week = +(ctx.params?.["week"] || 0);

    const { data: productCategories } = await store.dispatch(
      api.endpoints.productCategories.initiate(),
    );

    store.dispatch(
      api.endpoints.productsByCategories.initiate({
        categoryIds: getStandaloneProductCategoryIdsFromProductCategories(
          productCategories?.productCategories || [],
        ),
        week,
        year,
      }),
    );

    store.dispatch(
      api.endpoints.productsByIds.initiate({
        productIds: [PRODUCT_IDS.ONESUB],
        week,
        year,
      }),
    );

    store.dispatch(
      api.endpoints.pickAndMix.initiate({
        productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
        week,
        year,
      }),
    );

    await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()));

    return {
      props: {
        year,
        week,
      },
    };
  },
);

export const WeekEditorSuccessGetStaticPaths = () => {
  const dates = getNextCutoffs();

  const paths = dates.map(({ week, year }) => {
    return { params: { week: week.toString(), year: year.toString() } };
  });

  return {
    paths,
    fallback: "blocking",
  };
};

export const WeekEditorRestartedBillingGetStaticProps =
  storeWrapper.getStaticProps(() => async (ctx) => {
    const year = +(ctx.params?.["year"] || 0);
    const week = +(ctx.params?.["week"] || 0);

    return {
      props: {
        year,
        week,
      },
    };
  });

export const WeekEditorRestartedBillingGetStaticPaths = () => {
  const dates = getNextCutoffs();

  const paths = dates.map(({ week, year }) => {
    return { params: { week: week.toString(), year: year.toString() } };
  });

  return {
    paths,
    fallback: "blocking",
  };
};

export const OnTheMenuGetStaticProps = storeWrapper.getStaticProps(
  (store) => async (ctx) => {
    const currentDate = nextDeliveryWeekDateObj();

    const week = +(ctx.params?.["slug"]?.[0] || getWeek(currentDate));
    const year = +(ctx.params?.["slug"]?.[1] || getYear(currentDate));

    // pre-fetch the query data
    const commonArgs = { week, year };

    store.dispatch(
      api.endpoints.preselectedProductsForWeek.initiate(commonArgs),
    );

    store.dispatch(
      api.endpoints.pickAndMix.initiate({
        ...commonArgs,
        productTypeId: PRODUCT_TYPE_IDS.PICKANDMIX,
      }),
    );

    store.dispatch(
      api.endpoints.productsByCategories.initiate({
        ...commonArgs,
        categoryIds: [PRODUCT_CATEGORY_IDS.MEALBOX_LOGGED_OUT],
      }),
    );

    await Promise.all(store.dispatch(api.util.getRunningQueriesThunk()));

    return {
      props: {
        year,
        week,
      },
      revalidate: 1800, // 30 minutes
    };
  },
);

export const OnTheMenuGetStaticPaths = () => {
  const dates = getNextCutoffs();

  return {
    paths: [
      { params: { slug: [] } },
      ...dates.map(({ week, year }) => ({
        params: { slug: [week.toString(), year.toString()] },
      })),
    ],
    fallback: "blocking",
  };
};

interface MealboxProps {
  preference?: PreferencesQuery["preferences"][number];
  recipes?: PreviewRecipe[];
  allConcepts?: {
    name?: string;
    slug?: string;
  }[];
  concept?: IMealbox;
  draftMode?: boolean;
}

export const MealboxGetStaticProps = storeWrapper.getStaticProps(
  (store) =>
    async (
      ctx,
    ): Promise<{
      props: MealboxProps;
      notFound?: boolean;
      revalidate?: number;
    }> => {
      const slug = ctx.params?.["slug"]?.[0];
      const weekFromParams = ctx.params?.["slug"]?.[1];
      const yearFromParams = ctx.params?.["slug"]?.[2];

      const currentDate = nextDeliveryWeekDateObj();
      const week = weekFromParams
        ? parseInt(weekFromParams, 10)
        : getWeek(currentDate);

      const year = yearFromParams
        ? parseInt(yearFromParams, 10)
        : getYear(currentDate);

      const { preferences } = await store
        .dispatch(api.endpoints.preferences.initiate())
        .unwrap();

      const conceptPreferences = preferences.filter((p) =>
        isEqualStrings(p.preferenceTypeId, PREFERENCE_TYPE_IDS.CONCEPT),
      );

      const preference = conceptPreferences.find((p) =>
        isEqualStrings(getAttribute("preference_slug", p), slug),
      );

      const conceptContent = await getMealboxData(
        slug || "about-mealbox-page",
        {
          preview: ctx.preview,
        },
      );

      if (!preference) {
        return {
          props: {
            concept: conceptContent || null,
            draftMode: ctx.preview || false,
          },
          notFound: !conceptContent,
          revalidate: 600,
        };
      }

      const allConcepts = conceptPreferences.map((p) => ({
        slug: getAttribute("preference_slug", p),
        name: p.preferenceName,
      }));

      const preferenceId = preference ? preference.preferenceId : "";

      const recipes = getPreviewRecipes({ week, year, preferenceId });

      return {
        props: {
          preference,
          allConcepts,
          recipes,
          concept: conceptContent || null,
          draftMode: ctx.preview || false,
        },
        notFound: !preference && !conceptContent,
        revalidate: 600,
      };
    },
);

export const MealboxGetStaticPaths = getStaticPathsWithStore(
  (store) => async () => {
    const currentDate = nextDeliveryWeekDateObj();

    const week = getWeek(currentDate);
    const year = getYear(currentDate);

    const dates = getNextCutoffs();

    const productsByCategoriesQuery = await store
      .dispatch(
        api.endpoints.productsByCategories.initiate({
          categoryIds: [PRODUCT_CATEGORY_IDS.MEALBOX_LOGGED_OUT],
          week,
          year,
        }),
      )
      .unwrap();

    if (!productsByCategoriesQuery) {
      return {
        paths: [],
        fallback: "blocking",
      };
    }

    const productPaths = productsByCategoriesQuery.productsByCategories
      .flatMap((c) => c.products)
      .map((p) => getAttribute("slug", p))
      .filter(nonNullable);

    const paths = productPaths.flatMap((slug) => [
      { params: { slug: [slug] } },
      ...dates.map(({ week, year }) => ({
        params: { slug: [slug, week.toString(), year.toString()] },
      })),
    ]);

    return {
      paths,
      fallback: "blocking",
    };
  },
);
